import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Spinner } from "@chakra-ui/react";
import { CleanFiltersIcon } from "~misc/icons";
import Select from "~components/Form/components/Select";
import useGrupoProduto from "~/fetchs/useGrupoProdutoV2";
import useProduto from "~/fetchs/useProdutoV2";
import useFilial from "~/fetchs/useFilialV2";
import { getStoragekeysRules } from "~pages/PricesTableV2/helpers/storage";
import { useFormContext, Controller } from "react-hook-form";
import useMicrorregiao from "~fetchs/useMicrorregiaoV2";
import useGrupoSegmento from "~fetchs/useGrupoSegmentoV2";
import useSegmento from "~fetchs/useSegmentoV2";
import useCidades from "~fetchs/useCidadesV2";
import useCluster from "~fetchs/useClusterV2";

const FiltroPreco = ({ filtrosDisponiveis, nomeDoFiltro, loading }) => {
  const { watch, control, reset } = useFormContext();
  const watchProduto = watch("produto");
  const watchGrupoProduto = watch("grupoProduto");
  const watchGrupoSegmento = watch("grupoSegmento");

  const {
    grupoSegmentoDisponiveis,
    grupoMaterialDisponiveis,
    produtosDisponiveis,
    filiaisDisponiveis,
    microrregioesDisponiveis,
    segmentosDisponiveis,
    cidadesDisponiveis,
    clustersDisponiveis,
  } = filtrosDisponiveis;

  const optionDefault = useMemo(() => ({ label: "Todos", value: 0 }), []);
  const optionDefaultLoading = useMemo(
    () => ({
      label: loading ? "Carregando..." : optionDefault.label,
      value: optionDefault.value,
    }),
    [loading, optionDefault]
  );

  const keys = useMemo(() => getStoragekeysRules(nomeDoFiltro), [nomeDoFiltro]);

  const { optionMicrorregioes } = useMicrorregiao({
    microrregioesDisponiveis,
  });

  const { optionGruposProduto } = useGrupoProduto({
    camposOpt: ["nome"],
    gruposDisponiveis: grupoMaterialDisponiveis,
  });

  const { optionProdutos } = useProduto({
    grupoProduto: watchGrupoProduto,
    produtosDisponiveis: produtosDisponiveis,
    produto: watchProduto,
  });

  const { optionFiliais } = useFilial({
    filiaisDisponiveis,
  });

  const { optionsGrupoSegmento } = useGrupoSegmento({ grupoSegmentoDisponiveis: grupoSegmentoDisponiveis });

  const { optionsSegmento } = useSegmento(segmentosDisponiveis, watchGrupoSegmento);
  const { optionCidades } = useCidades({ cidadesDisponiveis });
  const { optionClusters } = useCluster({ clustersDisponiveis: clustersDisponiveis });

  const clear = () => {
    Object.values(keys).forEach((key) => {
      localStorage.removeItem(key);
    });

    reset({
      grupoProduto: 0,
      produto: 0,
      filial: 0,
      cidade: 0,
      microrregiao: 0,
      grupoSegmento: 0,
      segmento: 0,
      cluster: 0,
    });
  };

  const optMicrorregioes = useMemo(
    () => [optionDefaultLoading, ...optionMicrorregioes],
    [optionDefaultLoading, optionMicrorregioes]
  );
  const optGruposProduto = useMemo(
    () => [optionDefaultLoading, ...optionGruposProduto],
    [optionDefaultLoading, optionGruposProduto]
  );
  const optProdutos = useMemo(() => [optionDefaultLoading, ...optionProdutos], [optionDefaultLoading, optionProdutos]);
  const optFiliais = useMemo(() => [optionDefaultLoading, ...optionFiliais], [optionDefaultLoading, optionFiliais]);
  const optGrupoSegmento = useMemo(
    () => [optionDefaultLoading, ...optionsGrupoSegmento],
    [optionDefaultLoading, optionsGrupoSegmento]
  );
  const optSegmento = useMemo(
    () => [optionDefaultLoading, ...optionsSegmento],
    [optionDefaultLoading, optionsSegmento]
  );
  const optCidades = useMemo(() => [optionDefaultLoading, ...optionCidades], [optionDefaultLoading, optionCidades]);
  const optClusters = useMemo(() => [optionDefaultLoading, ...optionClusters], [optionDefaultLoading, optionClusters]);

  return (
    <>
      <section className="container prices-table-filter">
        <div className="form grid grid-cols-4 gap-x-3">
          <Controller
            name="microrregiao"
            control={control}
            render={({ field }) => (
              <Select
                label="Microrregião:"
                options={optMicrorregioes}
                spacingControl={true}
                isDisabled={loading}
                loading={loading}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.micro, JSON.stringify(selectedOption));
                }}
                value={optMicrorregioes?.find((option) => option.value === field.value) || optionDefault}
              />
            )}
          />

          <Controller
            name="grupoProduto"
            control={control}
            render={({ field }) => (
              <Select
                label="Grupo de Produto:"
                options={optGruposProduto}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.grupoProduto, JSON.stringify(selectedOption));
                }}
                isDisabled={loading}
                loading={loading}
                value={optGruposProduto?.find((option) => option.value === field.value) || optionDefault}
              />
            )}
          />

          <Controller
            name="produto"
            control={control}
            render={({ field }) => (
              <Select
                label="Produtos:"
                options={optProdutos}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.produto, JSON.stringify(selectedOption));
                }}
                isDisabled={loading}
                loading={loading}
                value={optProdutos?.find((option) => option.value === field.value) || optionDefault}
              />
            )}
          />

          <Controller
            name="filial"
            control={control}
            render={({ field }) => (
              <Select
                label="Centro:"
                options={optFiliais}
                spacingControl={true}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption.value);
                  localStorage.setItem(keys.filiais, JSON.stringify(selectedOption));
                }}
                isDisabled={loading}
                loading={loading}
                value={optFiliais?.find((option) => option.value === field.value) || optionDefault}
              />
            )}
          />

          {grupoSegmentoDisponiveis && grupoSegmentoDisponiveis.length > 0 && (
            <>
              <Controller
                name="grupoSegmento"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Grupo Segmento:"
                    options={optGrupoSegmento}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption.value);
                      localStorage.setItem(keys.segmento, JSON.stringify(selectedOption));
                    }}
                    value={optGrupoSegmento?.find((option) => option.value === 0) || optionDefault}
                    spacingControl={true}
                    isDisabled={loading}
                    loading={loading}
                  />
                )}
              />

              <Controller
                name="segmento"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Segmento:"
                    options={optSegmento}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption.value);
                      localStorage.setItem(keys.segmentoUnico, JSON.stringify(selectedOption));
                    }}
                    spacingControl={true}
                    isDisabled={loading}
                    loading={loading}
                    value={optSegmento?.find((option) => option.value === 0) || optionDefault}
                  />
                )}
              />
            </>
          )}

          {cidadesDisponiveis && cidadesDisponiveis.length > 0 && (
            <Controller
              name="cidade"
              control={control}
              render={({ field }) => (
                <Select
                  label="Cidade:"
                  options={optCidades}
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption.value);
                    localStorage.setItem(keys.cidade, JSON.stringify(selectedOption));
                  }}
                  spacingControl={true}
                  isDisabled={loading}
                  loading={loading}
                  value={optCidades?.find((option) => option.value === field.value) || optionDefault}
                />
              )}
            />
          )}

          {clustersDisponiveis && clustersDisponiveis.length > 0 && (
            <Controller
              name="cluster"
              control={control}
              render={({ field }) => (
                <Select
                  label="Cluster:"
                  options={optClusters}
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption.value);
                    localStorage.setItem(keys.cluster, JSON.stringify(selectedOption));
                  }}
                  spacingControl={true}
                  isDisabled={loading}
                  loading={loading}
                  value={optClusters?.find((option) => option.value === field.value) || optionDefault}
                />
              )}
            />
          )}

          <div className="col-span-4 flex ml-auto">
            <button type="button" className="clear-filter cursor-pointer" onClick={clear}>
              <span className="desc ml-0 mr-2 cursor-pointer">Limpar Filtros</span>
              <CleanFiltersIcon />
            </button>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="flex">
          {loading && (
            <p className="flex items-start gap-2 text-sm mt-4">
              <Spinner size={"sm"} />
              Verificando se há atualizações nas requisições
            </p>
          )}
        </div>
      </div>
    </>
  );
};

FiltroPreco.propTypes = {
  filtrosDisponiveis: PropTypes.shape({
    grupoSegmentoDisponiveis: PropTypes.array,
    grupoMaterialDisponiveis: PropTypes.array,
    produtosDisponiveis: PropTypes.array,
    filiaisDisponiveis: PropTypes.array,
    microrregioesDisponiveis: PropTypes.array,
    segmentosDisponiveis: PropTypes.array,
    cidadesDisponiveis: PropTypes.array,
    clustersDisponiveis: PropTypes.array,
  }),
  nomeDoFiltro: PropTypes.string,
  loading: PropTypes.bool,
};

export default FiltroPreco;
